import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { askAI } from './ai';
import { Card, CardHeader, Badge, EmptyState, SubmitBtn, SecondaryBtn, Field, Input, Select, ENTITY_LABELS } from './Shared';

const OPEN = ['open', 'in_progress', 'blocked'];
const PRIORITY = { 1: ['Urgent', 'red'], 2: ['Normal', 'gray'], 3: ['Low', 'gray'] };
const STATUS_LABEL = { proposed: 'Proposed', open: 'Open', in_progress: 'In progress', blocked: 'Blocked', snoozed: 'Snoozed', done: 'Done', dismissed: 'Passed', auto_resolved: 'Cleared' };
const STATUS_VARIANT = { proposed: 'purple', open: 'gray', in_progress: 'blue', blocked: 'amber', snoozed: 'gray', done: 'green', dismissed: 'gray', auto_resolved: 'green' };
const SOURCE_LABEL = { quickbooks: 'QuickBooks', royalties: 'Royalties', dispatch: 'Dispatch', academy: 'Academy', jurisdictions: 'Jurisdictions', data: 'Data feeds', ai: 'Idea', manual: 'Manual' };
const SOURCE_STATUS = { connected: ['Connected', 'green'], partial: ['Partial', 'amber'], manual: ['Manual entry', 'blue'], not_connected: ['Not connected', 'gray'] };

const todayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const fmtDate = d => (d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const ago = ts => { const m = Math.floor((Date.now() - new Date(ts)) / 60000); if (m < 60) return m + 'm ago'; if (m < 1440) return Math.floor(m / 60) + 'h ago'; return Math.floor(m / 1440) + 'd ago'; };

export default function Dashboard({ profile }) {
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [sources, setSources] = useState([]);
  const [people, setPeople] = useState([]);
  const [filter, setFilter] = useState('all');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [openId, setOpenId] = useState('');
  const [adding, setAdding] = useState(false);
  const [ideas, setIdeas] = useState({ loading: false, error: '' });

  const load = useCallback(async () => {
    const [{ data: a, error: e1 }, { data: m }, { data: s }, { data: p }] = await Promise.all([
      supabase.from('action_items').select('*').in('status', ['proposed', 'open', 'in_progress', 'blocked', 'snoozed']).order('priority').order('created_at'),
      supabase.rpc('action_metrics'),
      supabase.from('data_sources').select('*').order('sort'),
      supabase.rpc('action_assignees')
    ]);
    if (e1) setErr(e1.message);
    setItems(a || []); setMetrics(m || null); setSources(s || []); setPeople(p || []);
  }, []);

  useEffect(() => {
    // re-derive signals from live data, then show the queue
    supabase.rpc('actions_refresh').then(({ error }) => { if (error) setErr(error.message); load(); });
  }, [load]);

  const nameOf = id => (people.find(p => p.id === id) || {}).name;

  async function run(key, fn) {
    setBusy(key); setErr('');
    const { error } = await fn();
    setBusy('');
    if (error) return setErr(error.message);
    load();
  }
  const setStatus = (it, status) => {
    let note = null;
    if (status === 'blocked') { note = window.prompt('What is blocking this?'); if (!note) return; }
    run(it.id, () => supabase.rpc('action_set_status', { p_id: it.id, p_status: status, p_note: note }));
  };
  const markDone = it => { const o = window.prompt('What was the outcome? (optional)', ''); if (o === null) return; run(it.id, () => supabase.rpc('action_done', { p_id: it.id, p_outcome: o })); };
  const snooze = it => { const d = parseInt(window.prompt('Snooze for how many days?', '3'), 10); if (!d) return; run(it.id, () => supabase.rpc('action_snooze', { p_id: it.id, p_days: d })); };
  const pass = it => { const r = window.prompt('Why are we passing on this?'); if (!r) return; run(it.id, () => supabase.rpc('action_dismiss', { p_id: it.id, p_reason: r })); };
  const assign = (it, owner, due) => run(it.id, () => supabase.rpc('action_assign', { p_id: it.id, p_owner: owner || null, p_due: due || null, p_priority: null }));

  async function generateIdeas() {
    setIdeas({ loading: true, error: '' });
    try {
      const queue = items.filter(i => OPEN.includes(i.status)).slice(0, 15).map(i => '- ' + i.title).join('\n') || '(none)';
      const feeds = sources.map(s => `${s.label}: ${s.status}`).join('; ');
      const prompt = `You are the strategy desk for AO Group Holdings: Redline Fire Watch (staffing, FL/NJ/UT and national), Fire Watch Academy (training), Fire Watch Technologies (Judgr compliance software, TheFireWatchLog.com). The operating motto is "action vs inaction".
Current open actions:\n${queue}\n\nData feeds: ${feeds}.\nMetrics: ${JSON.stringify(metrics)}.\nPropose 4 NEW high-leverage ideas that are not already on the list, each executable by one person within two weeks. Reply with ONLY a JSON array of objects: {"title": short imperative, "detail": one or two sentences with the concrete first step, "priority": 1-3}.`;
      const out = await askAI(prompt, 2000);
      const arr = JSON.parse(out.slice(out.indexOf('['), out.lastIndexOf(']') + 1));
      for (const idea of arr.slice(0, 5)) {
        const { error } = await supabase.rpc('action_add', { p_title: String(idea.title).slice(0, 140), p_detail: String(idea.detail || '').slice(0, 400), p_priority: Number(idea.priority) || 2, p_kind: 'idea', p_status: 'proposed' });
        if (error) throw error;
      }
      setIdeas({ loading: false, error: '' }); load();
    } catch (e) { setIdeas({ loading: false, error: 'Could not generate ideas: ' + (e.message || e) }); }
  }

  const proposed = items.filter(i => i.status === 'proposed');
  const queue = items.filter(i => i.status !== 'proposed').filter(i => {
    if (filter === 'mine') return i.owner === profile.id;
    if (filter === 'unowned') return !i.owner;
    if (filter === 'blocked') return i.status === 'blocked';
    if (filter === 'overdue') return i.due_date && i.due_date < todayStr() && OPEN.includes(i.status);
    return true;
  });
  const m = metrics || {};

  return (
    <div>
      {err && <div className="save-msg save-msg-error" style={{ marginBottom: 14 }}>{err}</div>}

      <div className="metrics-grid">
        <div className="metric-card"><div className="metric-label">Open actions</div><div className="metric-value blue">{m.open ?? '–'}</div>
          <div className="metric-sub">{m.in_progress || 0} in progress, {m.blocked || 0} blocked</div></div>
        <div className="metric-card"><div className="metric-label">Overdue or urgent</div><div className="metric-value red">{(m.overdue || 0) + (m.urgent_open || 0) > 0 ? `${m.overdue || 0} / ${m.urgent_open || 0}` : '0'}</div>
          <div className="metric-sub">past due / marked urgent</div></div>
        <div className="metric-card"><div className="metric-label">Sitting without an owner</div><div className="metric-value amber">{m.unowned ?? '–'}</div>
          <div className="metric-sub">oldest open item {m.oldest_open_days ?? 0} days</div></div>
        <div className="metric-card"><div className="metric-label">Acted on, last 7 days</div><div className="metric-value green">{(m.done_7d || 0) + (m.cleared_7d || 0)}</div>
          <div className="metric-sub">{m.done_7d || 0} done, {m.cleared_7d || 0} cleared, {m.passed_7d || 0} passed</div></div>
      </div>

      {proposed.length > 0 && (
        <Card>
          <CardHeader title="Ideas waiting on a decision" hint={proposed.length + ' proposed'} />
          {proposed.map(it => (
            <div key={it.id} className="action-row">
              <div className="action-main">
                <div className="action-title">{it.title}</div>
                {it.detail && <div className="action-detail">{it.detail}</div>}
              </div>
              <div className="action-controls">
                <SubmitBtn sm loading={busy === it.id} onClick={() => run(it.id, () => supabase.rpc('action_accept', { p_id: it.id }))}>Make it an action</SubmitBtn>
                <SecondaryBtn sm onClick={() => pass(it)}>Pass</SecondaryBtn>
              </div>
            </div>
          ))}
        </Card>
      )}

      <Card>
        <CardHeader title="Action queue" hint={queue.length + ' shown'}
          action={<div style={{ display: 'flex', gap: 8 }}>
            <SecondaryBtn sm onClick={generateIdeas}>{ideas.loading ? 'Thinking…' : 'Generate ideas'}</SecondaryBtn>
            <SubmitBtn sm onClick={() => setAdding(a => !a)}>{adding ? 'Close' : 'Add action'}</SubmitBtn>
          </div>} />
        {ideas.error && <div className="save-msg save-msg-error" style={{ marginBottom: 12 }}>{ideas.error}</div>}
        {adding && <AddAction people={people} me={profile.id} onDone={() => { setAdding(false); load(); }} onError={setErr} />}
        <div className="tab-bar" style={{ marginBottom: 6 }}>
          {[['all', 'All'], ['mine', 'Mine'], ['unowned', 'No owner'], ['blocked', 'Blocked'], ['overdue', 'Overdue']].map(([k, l]) => (
            <button key={k} className={'tab-item' + (filter === k ? ' active' : '')} onClick={() => setFilter(k)}>{l}</button>))}
        </div>
        {!queue.length && <EmptyState text={filter === 'all' ? 'Nothing open. Press Generate ideas, or add an action.' : 'Nothing matches this filter.'} />}
        {queue.map(it => (
          <div key={it.id}>
            <div className="action-row">
              <span className={'action-pri pri-' + it.priority} title={PRIORITY[it.priority][0]} />
              <div className="action-main" onClick={() => setOpenId(openId === it.id ? '' : it.id)} style={{ cursor: 'pointer' }}>
                <div className="action-title">{it.title}</div>
                {it.detail && <div className="action-detail">{it.detail}</div>}
                <div className="action-meta">
                  <Badge variant={STATUS_VARIANT[it.status]}>{STATUS_LABEL[it.status]}{it.status === 'snoozed' ? ' until ' + fmtDate(it.snooze_until) : ''}</Badge>
                  <span>{SOURCE_LABEL[it.source] || it.source}</span>
                  {it.entity && <span>{ENTITY_LABELS[it.entity] || it.entity}</span>}
                  <span>opened {ago(it.created_at)}</span>
                  {it.reopen_count > 0 && <span style={{ color: 'var(--danger-text)' }}>reopened {it.reopen_count}x</span>}
                  {it.snooze_count > 1 && <span style={{ color: 'var(--amber-text)' }}>snoozed {it.snooze_count}x</span>}
                </div>
              </div>
              <div className="action-controls">
                <select className="field-select action-owner" value={it.owner || ''} onChange={e => assign(it, e.target.value, null)} aria-label="Owner">
                  <option value="">No owner</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input type="date" className="field-input action-due" value={it.due_date || ''} onChange={e => assign(it, it.owner, e.target.value)} aria-label="Due date"
                  style={it.due_date && it.due_date < todayStr() ? { borderColor: 'var(--red)' } : null} />
                {it.status !== 'in_progress' && <SecondaryBtn sm onClick={() => setStatus(it, 'in_progress')}>Start</SecondaryBtn>}
                {it.status !== 'blocked' && <SecondaryBtn sm onClick={() => setStatus(it, 'blocked')}>Block</SecondaryBtn>}
                {it.status === 'snoozed' || it.status === 'blocked' ? <SecondaryBtn sm onClick={() => setStatus(it, 'open')}>Reopen</SecondaryBtn> : <SecondaryBtn sm onClick={() => snooze(it)}>Snooze</SecondaryBtn>}
                <SecondaryBtn sm onClick={() => pass(it)}>Pass</SecondaryBtn>
                <SubmitBtn sm loading={busy === it.id} onClick={() => markDone(it)}>Done</SubmitBtn>
              </div>
            </div>
            {openId === it.id && <Trail item={it} nameOf={nameOf} onError={setErr} />}
          </div>
        ))}
      </Card>

      <Card>
        <CardHeader title="Data feeding the machine" hint={sources.filter(s => s.status === 'connected').length + ' of ' + sources.length + ' connected'} />
        <table className="data-table">
          <thead><tr><th>Source</th><th>Tells us</th><th>Status</th></tr></thead>
          <tbody>{sources.map(s => (
            <tr key={s.key}>
              <td style={{ fontWeight: 600 }}>{s.label}</td>
              <td style={{ color: 'var(--text-secondary)' }}>{s.feeds}{s.note ? ' (' + s.note + ')' : ''}</td>
              <td><Badge variant={SOURCE_STATUS[s.status][1]}>{SOURCE_STATUS[s.status][0]}</Badge>{s.last_sync_at && <span className="card-hint"> synced {ago(s.last_sync_at)}</span>}</td>
            </tr>))}</tbody>
        </table>
      </Card>
    </div>
  );
}

function Trail({ item, nameOf, onError }) {
  const [events, setEvents] = useState([]);
  const [note, setNote] = useState('');
  const [names, setNames] = useState({});
  const load = useCallback(async () => {
    const { data } = await supabase.from('action_events').select('*').eq('action_id', item.id).order('created_at');
    setEvents(data || []);
  }, [item.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { supabase.rpc('action_assignees').then(({ data }) => setNames(Object.fromEntries((data || []).map(p => [p.id, p.name])))); }, []);
  async function post() {
    if (!note.trim()) return;
    const { error } = await supabase.rpc('action_comment', { p_id: item.id, p_note: note });
    if (error) return onError(error.message);
    setNote(''); load();
  }
  return (
    <div className="action-trail">
      {!events.length && <div className="card-hint">No activity yet.</div>}
      {events.map(e => (
        <div key={e.id} className="action-event"><span className="card-hint">{ago(e.created_at)}</span> <b>{e.actor ? (names[e.actor] || 'Someone') : 'System'}</b> {e.event === 'comment' ? 'commented' : e.event}{e.note ? ': ' + e.note : ''}</div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input className="field-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Add a comment or update" onKeyDown={e => e.key === 'Enter' && post()} />
        <SecondaryBtn sm onClick={post}>Post</SecondaryBtn>
      </div>
    </div>
  );
}

function AddAction({ people, me, onDone, onError }) {
  const [f, setF] = useState({ title: '', detail: '', owner: me, due: '', priority: '2' });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    const { error } = await supabase.rpc('action_add', { p_title: f.title, p_detail: f.detail || null, p_priority: Number(f.priority), p_due: f.due || null, p_kind: 'manual', p_status: 'open', p_owner: f.owner || null });
    setSaving(false);
    if (error) return onError(error.message);
    onDone();
  }
  return (
    <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
      <div className="form-grid">
        <Field label="What needs to happen?" full><Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} placeholder="Call Orlando FD about the pilot" /></Field>
        <Field label="Detail" full><Input value={f.detail} onChange={e => setF({ ...f, detail: e.target.value })} /></Field>
        <Field label="Owner"><Select value={f.owner} onChange={e => setF({ ...f, owner: e.target.value })} placeholder="No owner" options={people.map(p => ({ value: p.id, label: p.name }))} /></Field>
        <Field label="Due"><Input type="date" value={f.due} onChange={e => setF({ ...f, due: e.target.value })} /></Field>
        <Field label="Priority"><Select value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })} options={[{ value: '1', label: 'Urgent' }, { value: '2', label: 'Normal' }, { value: '3', label: 'Low' }]} /></Field>
      </div>
      <div className="form-actions" style={{ borderTop: 'none', paddingTop: 0 }}><SubmitBtn onClick={save} loading={saving}>Add action</SubmitBtn></div>
    </div>
  );
}
